class VaillantProductGallery extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.dots = [...this.querySelectorAll('[data-gallery-dot]')];
    this.dotsNav = this.querySelector('[data-gallery-dots]');
    this.dotsShell = this.querySelector('.vaillant-product-gallery__dots-shell');
    this.slidesContainer = this.querySelector('[data-gallery-slides]');
    this.zoomTriggers = [...this.querySelectorAll('[data-gallery-zoom-trigger]')];
    this.mobileMediaQuery = window.matchMedia('(max-width: 989px)');
    this.activeSlideIndex = 0;
    this.dotsPositionFrame = null;

    this.handleSlidesScroll = this.handleSlidesScroll.bind(this);
    this.handleMobileQueryChange = this.handleMobileQueryChange.bind(this);
    this.handleWindowScroll = this.handleWindowScroll.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);

    this.zoomTriggers.forEach((trigger) => {
      trigger.addEventListener('click', this.handleZoomToggle);
      trigger.addEventListener('mousemove', this.handleZoomPan);
    });

    document.addEventListener('keydown', this.handleKeyDown);

    if (this.slides.length <= 1) return;

    this.setActiveDot(0);
    this.setupGalleryNavigation();
    this.mobileMediaQuery.addEventListener('change', this.handleMobileQueryChange);
    this.querySelectorAll('.vaillant-product-gallery__image').forEach((image) => {
      if (image.complete) return;
      image.addEventListener('load', () => this.scheduleDotsPosition(), { once: true });
    });
  }

  disconnectedCallback() {
    this.zoomTriggers.forEach((trigger) => {
      trigger.removeEventListener('click', this.handleZoomToggle);
      trigger.removeEventListener('mousemove', this.handleZoomPan);
    });
    document.removeEventListener('keydown', this.handleKeyDown);
    this.dots.forEach((dot) => {
      dot.removeEventListener('click', this.handleDotClick);
    });
    this.slidesContainer?.removeEventListener('scroll', this.handleSlidesScroll);
    this.mobileMediaQuery?.removeEventListener('change', this.handleMobileQueryChange);
    window.removeEventListener('scroll', this.handleWindowScroll);
    window.removeEventListener('resize', this.handleWindowScroll);
    window.cancelAnimationFrame(this.dotsPositionFrame);
    this.observer?.disconnect();
  }

  setupGalleryNavigation() {
    this.observer?.disconnect();
    this.slidesContainer?.removeEventListener('scroll', this.handleSlidesScroll);
    this.dots.forEach((dot) => {
      dot.removeEventListener('click', this.handleDotClick);
    });

    if (this.mobileMediaQuery.matches) {
      this.classList.add('vaillant-product-gallery--mobile-slider');
      window.removeEventListener('scroll', this.handleWindowScroll);
      window.removeEventListener('resize', this.handleWindowScroll);
      if (this.dotsShell) {
        this.dotsShell.hidden = false;
        this.dotsShell.style.right = '';
        this.dotsShell.style.top = '';
        this.dotsShell.style.bottom = '';
        this.dotsShell.style.left = '';
      }
      this.slidesContainer?.addEventListener('scroll', this.handleSlidesScroll, { passive: true });
      return;
    }

    this.classList.remove('vaillant-product-gallery--mobile-slider');
    window.addEventListener('scroll', this.handleWindowScroll, { passive: true });
    window.addEventListener('resize', this.handleWindowScroll, { passive: true });
    this.dots.forEach((dot) => {
      dot.addEventListener('click', this.handleDotClick);
    });

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(this.handleIntersection, {
        root: null,
        threshold: 0.55,
      });
      this.slides.forEach((slide) => this.observer.observe(slide));
    }

    this.scheduleDotsPosition();
  }

  handleWindowScroll() {
    if (this.mobileMediaQuery.matches) return;
    this.scheduleDotsPosition();
  }

  scheduleDotsPosition() {
    if (this.mobileMediaQuery.matches || !this.dotsShell || !this.dotsNav) return;

    window.cancelAnimationFrame(this.dotsPositionFrame);
    this.dotsPositionFrame = window.requestAnimationFrame(() => {
      this.updateDotsTarget();
    });
  }

  getCssLengthPx(customProperty, fallbackPx) {
    const rawValue = getComputedStyle(this).getPropertyValue(customProperty).trim();
    if (!rawValue) return fallbackPx;

    const numericValue = Number.parseFloat(rawValue);
    if (Number.isNaN(numericValue)) return fallbackPx;

    if (rawValue.endsWith('rem')) {
      const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
      return numericValue * rootFontSize;
    }

    if (rawValue.endsWith('px')) return numericValue;

    return numericValue;
  }

  updateDotsTarget() {
    if (this.mobileMediaQuery.matches || !this.dotsShell || !this.dotsNav || !this.slides.length) return;

    const lastSlide = this.slides[this.slides.length - 1];
    if (!lastSlide) return;

    const galleryRect = this.getBoundingClientRect();
    const inset = this.getCssLengthPx('--gallery-dots-inset', 48);
    const viewportOffset = this.getCssLengthPx('--gallery-dots-viewport-offset', 62);
    const dotsHeight = this.dotsNav.offsetHeight;

    if (galleryRect.bottom <= 0 || galleryRect.top >= window.innerHeight) {
      this.dotsShell.hidden = true;
      return;
    }

    this.dotsShell.hidden = false;
    this.dotsShell.style.transform = 'none';
    this.dotsShell.style.right = `${Math.max(0, window.innerWidth - galleryRect.right + inset)}px`;
    this.dotsShell.style.left = 'auto';

    const lastSlideBottom = lastSlide.getBoundingClientRect().bottom;
    const fixedDotsTop = window.innerHeight - viewportOffset - dotsHeight;
    const maxDotsTop = lastSlideBottom - dotsHeight - inset;

    if (fixedDotsTop > maxDotsTop) {
      this.dotsShell.style.bottom = 'auto';
      this.dotsShell.style.top = `${Math.max(0, maxDotsTop)}px`;
      return;
    }

    this.dotsShell.style.top = 'auto';
    this.dotsShell.style.bottom = `${viewportOffset}px`;
  }

  handleMobileQueryChange() {
    this.clearAllZoom();
    if (this.slidesContainer) {
      this.slidesContainer.scrollLeft = 0;
    }
    this.setupGalleryNavigation();
    this.setActiveDot(0);
  }

  handleZoomToggle = (event) => {
    const trigger = event.currentTarget;
    const wasZoomed = trigger.classList.contains('is-zoomed');

    this.zoomTriggers.forEach((item) => {
      if (item !== trigger) this.setZoomState(item, false);
    });

    if (wasZoomed) {
      this.setZoomState(trigger, false);
      return;
    }

    this.setZoomOrigin(trigger, event);
    this.setZoomState(trigger, true);
  };

  handleZoomPan = (event) => {
    const trigger = event.currentTarget;
    if (!trigger.classList.contains('is-zoomed')) return;
    this.setZoomOrigin(trigger, event);
  };

  handleKeyDown(event) {
    if (event.code !== 'Escape') return;
    this.clearAllZoom();
  }

  setZoomOrigin(trigger, event) {
    const rect = trigger.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    trigger.style.setProperty('--zoom-origin-x', `${x}%`);
    trigger.style.setProperty('--zoom-origin-y', `${y}%`);
  }

  setZoomState(trigger, isZoomed) {
    trigger.classList.toggle('is-zoomed', isZoomed);
    trigger.setAttribute('aria-pressed', isZoomed ? 'true' : 'false');

    const slide = trigger.closest('[data-gallery-slide]');
    slide?.classList.toggle('is-zoomed', isZoomed);

    this.classList.toggle(
      'vaillant-product-gallery--has-zoomed-slide',
      this.zoomTriggers.some((item) => item.classList.contains('is-zoomed'))
    );
  }

  clearAllZoom() {
    this.zoomTriggers.forEach((trigger) => this.setZoomState(trigger, false));
  }

  clearZoomForSlide(slide) {
    const trigger = slide?.querySelector('[data-gallery-zoom-trigger]');
    if (trigger) this.setZoomState(trigger, false);
  }

  handleDotClick = (event) => {
    this.clearAllZoom();

    const index = Number(event.currentTarget.dataset.slideIndex);
    const slide = this.slides[index];

    if (!slide) return;

    slide.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.setActiveDot(index);
    this.scheduleDotsPosition();
  };

  handleSlidesScroll() {
    this.clearAllZoom();

    const index = this.getActiveSlideIndexFromScroll();
    if (index >= 0) this.setActiveDot(index);
  }

  getActiveSlideIndexFromScroll() {
    if (!this.slidesContainer || !this.slides.length) return 0;

    const slideWidth = this.slides[0].getBoundingClientRect().width;
    if (!slideWidth) return 0;

    return Math.min(
      this.slides.length - 1,
      Math.max(0, Math.round(this.slidesContainer.scrollLeft / slideWidth))
    );
  }

  handleIntersection = (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting || entry.intersectionRatio < 0.55) {
        this.clearZoomForSlide(entry.target);
      }
    });

    const visibleEntries = entries
      .filter((entry) => entry.isIntersecting)
      .sort((left, right) => right.intersectionRatio - left.intersectionRatio);

    if (!visibleEntries.length) return;

    const index = this.slides.indexOf(visibleEntries[0].target);
    if (index >= 0) this.setActiveDot(index);
  };

  setActiveDot(index) {
    this.activeSlideIndex = index;

    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    const progressBar = this.querySelector('[data-gallery-progress-bar]');
    if (progressBar) {
      progressBar.style.setProperty('--gallery-progress-index', index);
    }
  }
}

if (!customElements.get('vaillant-product-gallery')) {
  customElements.define('vaillant-product-gallery', VaillantProductGallery);
}
